import React from 'react';

import {Card, List, ListItem, Button, Col, Row, Icon, Label, NoData, Text, Tag, Space, showConfirm, ButtonGroup, Modal} from '@mc/ui-v4';

import {mcEdit, mcNew, mcDelete} from '@mc/react-ui-icons';
import {UserRightsVO, ActionConfigVO, RightsApi, LayerVO} from '../../../api';
import {OpravneniModal, SingleOpravneniProps} from './OpravneniModal';
import {QueryClient, useQueryClient} from 'react-query';
import {CACHE_KEY_LAYER_OPRAVNENI, CACHE_KEY_USER_LAYER_OPRAVNENI} from '../../../queryKeys';
import {printFormatterUredniOsobaNazevEOS} from '../../../helpers/Formatter';
import {actionApiCall, rffFormSubmit} from '@mc/react-app-v4';
import './OpravneniConfiguration.scss';
import classNames from 'classnames';
import {useAppConfig} from '@mc/react-app-base';

type OpravneniProps = {
    opravneniItems: UserRightsVO[];
    isUserVlastnikOrAdmin?: boolean;
    topicLayers?: LayerVO[];
    showLayers?: boolean;
    layer?: LayerVO;
    akce?: ActionConfigVO[];
};
export const OpravneniConfiguration: React.FC<OpravneniProps> = ({opravneniItems, isUserVlastnikOrAdmin, topicLayers, showLayers, layer, akce}) => {
    let appConfig = useAppConfig();
    const queryClient: QueryClient = useQueryClient();

    const handleCreateUpdateOpravneni = (initialValues?: UserRightsVO) => {
        Modal.show(({onClose}) => (
            <OpravneniModal
                onClose={onClose}
                initialValues={
                    initialValues
                        ? {
                              vlastnikVrstvy: initialValues.layers[0].vlastnikVrstvy,
                              entita: {type: 'PRACOVNIK', pracovnik: initialValues.pracovnik},
                              actionList: initialValues.layers[0]?.actionRights?.actionList || [],
                          }
                        : {actionList: []}
                }
                layerLabel={layer.label}
                akce={akce}
                onSubmit={async data => {
                    return rffFormSubmit(async () => {
                        document.getElementsByClassName('textInfo')[0]?.classList.remove('d-none');
                        await RightsApi.updateUserRights(
                            layer.id,
                            true,
                            data.vlastnikVrstvy,
                            data.actionList.map(item => item.id),
                            getIdPracovnikuFromData(data),
                        );
                        await queryClient.invalidateQueries([CACHE_KEY_LAYER_OPRAVNENI, layer.id]);
                        await queryClient.invalidateQueries([CACHE_KEY_USER_LAYER_OPRAVNENI, layer.id]);
                        onClose();
                        document.getElementsByClassName('textInfo')[0]?.classList.add('d-none');
                    });
                }}
            />
        ));
    };

    const getIdPracovnikuFromData = (data: SingleOpravneniProps): number[] => {
        switch (data.entita.type) {
            case 'PRACOVNIK':
                return [data.entita.pracovnik.pracovnikId];
            case 'ORG_JEDNOTKA':
                return data.entita.organizacniJednotka.pracovnici.map(p => p.pracovnikId);
            case 'ORG_ROLE':
                return data.entita.organizacniRole.pracovnici.map(p => p.pracovnikId);
        }
    };

    const handleRemovePrava = async (pracovnikId: number) => {
        const result = await showConfirm({
            title: 'Odstranit uživatelské oprávnění',
            message: 'Opravdu chcete smazat uživatelské oprávnění?',
            intent: 'danger',
        });
        document.getElementsByClassName('textInfo')[0]?.classList.remove('d-none');
        if (result) {
            await actionApiCall(() => RightsApi.deleteUserRights(layer.id, pracovnikId, true, true));
            await queryClient.invalidateQueries([CACHE_KEY_LAYER_OPRAVNENI, layer.id]);
            await queryClient.invalidateQueries([CACHE_KEY_USER_LAYER_OPRAVNENI, layer.id]);
        }
        document.getElementsByClassName('textInfo')[0]?.classList.add('d-none');
    };

    const renderSingleLayer = (opravneniItem: UserRightsVO) => {
        const layerRight = opravneniItem.layers[0];
        return (
            <Row noGutters className={'py-0 h-100'}>
                <Col className={'my-auto'}>
                    {layerRight.actionRights.allActions ? (
                        <Text intent={'warning'}>všechny akce</Text>
                    ) : (
                        <>
                            {!opravneniItems || opravneniItems?.length === 0 || layerRight.actionRights.noActions ? (
                                <Text intent={'secondary'}>pouze prohlížení</Text>
                            ) : (
                                <Space>
                                    {layerRight.actionRights.actionList.map(akce => (
                                        <Tag key={akce.id}>{akce.nazev}</Tag>
                                    ))}
                                </Space>
                            )}
                        </>
                    )}
                </Col>
            </Row>
        );
    };

    const renderAllLayers = (opravneniItem: UserRightsVO) => {
        return (
            <List compact noHorizontalPadding className={'negative-margin'}>
                {opravneniItem.layers.map(vrstva => {
                    const currentLayer = topicLayers?.find(layer => layer.id === vrstva.layer.id);
                    const layerImgPath = currentLayer?.style?.marker || currentLayer?.layerImage;
                    return (
                        <ListItem key={vrstva.layer.id}>
                            <Row noGutters className={'py-0'}>
                                <Col className={'align-items-center d-flex my-auto'} xs={1}>
                                    {layerImgPath && (
                                        <img
                                            className={'icon conf-layer-image'}
                                            src={appConfig.serverContextPath + '/api/files/img/' + layerImgPath}
                                            alt={vrstva.layer.label}
                                        />
                                    )}
                                </Col>
                                <Col xs={3} className={'my-auto'}>
                                    {vrstva.layer.label}
                                </Col>
                                <Col className={'my-auto'}>
                                    {vrstva.actionRights.allActions ? (
                                        <Text intent={'warning'}>všechny akce</Text>
                                    ) : (
                                        <>
                                            {!opravneniItems || opravneniItems?.length === 0 || vrstva.actionRights.noActions ? (
                                                <Text intent={'secondary'}>pouze prohlížení</Text>
                                            ) : (
                                                <Space>
                                                    {vrstva.actionRights.actionList.map(akce => (
                                                        <Tag key={akce.id}>{akce.nazev}</Tag>
                                                    ))}
                                                </Space>
                                            )}
                                        </>
                                    )}
                                </Col>
                            </Row>
                        </ListItem>
                    );
                })}
            </List>
        );
    };

    return (
        <Card
            mode={'view'}
            title={showLayers ? 'Oprávnění uživatelů' : 'Oprávnění uživatelů na vrstvu'}
            extra={!showLayers && isUserVlastnikOrAdmin && <Button style={'link'} onClick={() => handleCreateUpdateOpravneni()} icon={<Icon icon={mcNew} />} />}
        >
            <Text className={'d-none textInfo ml-2'} type={'block'} intent={'warning'}>
                Promítnutí oprávnění může trvat několik minut.
            </Text>
            <List mode={'labels'} extraSize={showLayers ? 1 : 2}>
                <ListItem>
                    <Row className={'py-0'}>
                        <Col xs={4}>
                            <Label>Uživatel</Label>
                        </Col>
                        <Col>
                            {/*Row je zanořeno 2* aby seděli velikosti polí v headeru a v listu... (máme vnořené listy)*/}
                            <Row noGutters className={'py-0'}>
                                {showLayers && (
                                    <Col xs={4}>
                                        <Label>Vrstva</Label>
                                    </Col>
                                )}
                                <Col>
                                    <Label>Povolené akce</Label>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </ListItem>
            </List>
            <List extraSize={showLayers ? 1 : 2}>
                {!opravneniItems || opravneniItems?.length === 0 ? (
                    <NoData />
                ) : (
                    <>
                        {opravneniItems.map((opravneniItem, index) => {
                            return (
                                <ListItem
                                    className={classNames({['custom-rounded-bottom']: index === opravneniItems.length - 1})}
                                    key={`${index}-${opravneniItem.pracovnik?.pracovnikId}`}
                                    extra={
                                        !showLayers && (
                                            <ButtonGroup className={'float-right'}>
                                                {isUserVlastnikOrAdmin && (
                                                    <Button
                                                        className={'ml-auto'}
                                                        key={`edit-${index}-${opravneniItem.pracovnik?.pracovnikId}`}
                                                        style={'link'}
                                                        onClick={() => handleCreateUpdateOpravneni(opravneniItem)}
                                                        showOnHover
                                                        icon={<Icon icon={mcEdit} />}
                                                    />
                                                )}
                                                {isUserVlastnikOrAdmin && (
                                                    <Button
                                                        icon={<Icon icon={mcDelete} />}
                                                        style={'link'}
                                                        key={`3-dots-${index}-${opravneniItem.pracovnik?.pracovnikId}`}
                                                        showOnHover
                                                        title={'Smazat uživatelské oprávnění'}
                                                        onClick={() => handleRemovePrava(opravneniItem.pracovnik?.pracovnikId)}
                                                    />
                                                )}
                                            </ButtonGroup>
                                        )
                                    }
                                >
                                    <Row className={'py-0'}>
                                        <Col xs={4} className={'d-flex flex-column negative-margin'}>
                                            <Space>
                                                <Text strong>{printFormatterUredniOsobaNazevEOS(opravneniItem.pracovnik)}</Text>
                                                {opravneniItem.layers[0].vlastnikVrstvy && (
                                                    <Tag intent={'warning'} className={'my-auto'}>
                                                        Vlastník
                                                    </Tag>
                                                )}
                                            </Space>
                                            <Text intent={'secondary'}>{opravneniItem.pracovnik?.email}</Text>
                                        </Col>
                                        <Col className={'negative-margin'}>
                                            {showLayers ? renderAllLayers(opravneniItem) : renderSingleLayer(opravneniItem)}
                                        </Col>
                                    </Row>
                                </ListItem>
                            );
                        })}
                    </>
                )}
            </List>
        </Card>
    );
};
